"use client"

import { useEffect } from "react"
import { isInCriticalAuthFlow } from "@/lib/auth-flow-context"

/**
 * Register service worker for PWA functionality
 * This enables the app to be installable on home screen
 * and handles automatic update detection
 */
export function ServiceWorkerRegister() {
  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
      return
    }

    let registration: ServiceWorkerRegistration | null = null
    let updateNotified = false // Flag to prevent duplicate notifications
    let currentSWVersion: string | null = null // Track current SW version

    // Helper to get SW version
    async function getSWVersion(): Promise<string | null> {
      const controller = navigator.serviceWorker.controller
      if (!controller) return null

      return new Promise((resolve) => {
        const messageChannel = new MessageChannel()
        messageChannel.port1.onmessage = (event) => {
          resolve(event.data.version || null)
        }
        controller.postMessage(
          { type: 'GET_VERSION' },
          [messageChannel.port2]
        )
      })
    }

    // Initialize SW version on first load
    const initVersion = async () => {
      currentSWVersion = await getSWVersion()
      console.log("[SW Register] Current SW version:", currentSWVersion)
    }
    initVersion()

    // Register service worker
    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        registration = reg
        console.log("Service Worker registered:", reg.scope)

        // Check for updates every 60 seconds
        // This helps both desktop and iOS to detect updates more quickly
        // But skip if user is in critical auth flow
        const updateInterval = setInterval(() => {
          if (!isInCriticalAuthFlow()) {
            reg.update().catch((err) => {
              console.error("Service Worker update check failed:", err)
            })
          }
        }, 60 * 1000)

        // Listen for new service worker being installed
        reg.addEventListener("updatefound", () => {
          const newWorker = reg.installing
          console.log("Service Worker update found")

          // Only set up listener if we haven't already notified
          if (newWorker && !updateNotified) {
            newWorker.addEventListener("statechange", async () => {
              // When the new service worker is installed and waiting
              if (
                newWorker.state === "installed" &&
                navigator.serviceWorker.controller &&
                !updateNotified
              ) {
                // Get version of new worker
                const newVersion = await getSWVersion()

                // Only notify if version actually changed
                if (newVersion && newVersion !== currentSWVersion) {
                  console.log(`New SW version detected: ${currentSWVersion} -> ${newVersion}`)
                  updateNotified = true // Prevent duplicate notifications
                  currentSWVersion = newVersion
                  // Dispatch custom event to show update notification
                  window.dispatchEvent(new Event("sw-update-available"))
                } else {
                  console.log("SW reinstalled but version unchanged, skipping notification")
                }
              }
            })
          }
        })

        // Cleanup interval on unmount
        return () => clearInterval(updateInterval)
      })
      .catch((error) => {
        console.error("Service Worker registration failed:", error)
      })

    // Listen for the new service worker taking control
    // This happens after skipWaiting() is called
    navigator.serviceWorker.addEventListener("controllerchange", async () => {
      console.log("New Service Worker activated")

      const newVersion = await getSWVersion()

      // Only reload if version changed AND not in auth flow
      if (newVersion && newVersion !== currentSWVersion && !isInCriticalAuthFlow()) {
        console.log(`SW version changed (${currentSWVersion} -> ${newVersion}), reloading`)
        currentSWVersion = newVersion
        window.location.reload()
      } else if (isInCriticalAuthFlow()) {
        console.log("Deferring reload due to critical auth flow")
      } else {
        console.log("SW activated but version unchanged, skipping reload")
      }
    })

    // Check for updates when page becomes visible (user returns to app)
    // This is especially important for iOS PWAs
    // But skip if user is in critical auth flow
    const handleVisibilityChange = () => {
      if (!document.hidden && registration && !isInCriticalAuthFlow()) {
        console.log("Page became visible, checking for Service Worker updates")
        registration.update().catch((err) => {
          console.error("Service Worker visibility update check failed:", err)
        })
      } else if (!document.hidden && isInCriticalAuthFlow()) {
        console.log("Skipping Service Worker update check during critical auth flow")
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [])

  // This component doesn't render anything
  return null
}
